import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Input, Button } from '../components';
import { useAuth } from '../hooks';
import { ICON_STROKE_WIDTH } from '../constants';
import { authService } from '../auth/authService';
import Unauthorized from '../components/Unauthorized';
import { isPatientMode, setPatientMode } from '../utils/patientMode';
import '../theme/index.css';
import './Login.css';

interface LoginFormData {
  username: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
    search?: string;
  };
}

const PatientLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { patientLogin, isLoading, error, clearError } = useAuth();

  // Get StudyInstanceUIDs from URL or location state
  const getStudyInstanceUIDs = () => {
    // First check URL params
    const urlParam = searchParams.get('StudyInstanceUIDs');
    if (urlParam) {
      return urlParam;
    }

    // Then check location state
    const from = (location.state as LocationState)?.from;
    if (from?.pathname?.includes('/viewer')) {
      const pathParts = from.pathname.split('?');
      if (pathParts.length > 1) {
        const params = new URLSearchParams(pathParts[1]);
        return params.get('StudyInstanceUIDs');
      }
    }

    // Check from.search if available
    if (from?.search) {
      const params = new URLSearchParams(from.search);
      return params.get('StudyInstanceUIDs');
    }

    return null;
  };

  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState<Partial<LoginFormData>>({});

  const studyInstanceUIDs = getStudyInstanceUIDs();
  const isAuthenticated = authService.isPatientSessionActive();
  const patientMode = isPatientMode();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // If StudyInstanceUIDs are present, redirect to viewer
      if (studyInstanceUIDs) {
        navigate(`/viewer?StudyInstanceUIDs=${studyInstanceUIDs}`, { replace: true });
      } else if (!patientMode) {
        // Regular users can access root route
        navigate('/', { replace: true });
      }
      // Authenticated patient without StudyInstanceUIDs will see Unauthorized component below
    }
  }, [navigate, location, studyInstanceUIDs, isAuthenticated, patientMode]);

  // If accessing patient login directly without StudyInstanceUIDs, show unauthorized
  // This prevents direct access to /patient/login without StudyInstanceUIDs parameter
  if (!studyInstanceUIDs) {
    return <Unauthorized />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name as keyof LoginFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Clear general error
    if (error) {
      clearError();
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormData> = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const response = await patientLogin({
      username: formData.username,
      password: formData.password,
    });

    if (response.success) {
      // Mark user as patient for read-only mode (sessionStorage, tab-scoped)
      setPatientMode(true);

      // Redirect to viewer with StudyInstanceUIDs if present
      if (studyInstanceUIDs) {
        navigate(`/viewer?StudyInstanceUIDs=${studyInstanceUIDs}`, { replace: true });
      } else {
        // Otherwise redirect to default route
        navigate('/', { replace: true });
      }
    }
  };

  return (
    <div className="login-page-light">
      <div className="login-inner">
        <div className="login-logo-wrap">
          <div className="login-logo-icon">
            <Sparkles
              strokeWidth={ICON_STROKE_WIDTH}
              className="login-logo-icon-svg"
            />
          </div>
          <span className="login-logo-text">RadiologyIQ</span>
        </div>

        <div className="login-card-light">
          <div className="login-card-header">
            <h1 className="login-card-title">Patient login</h1>
            <p className="login-card-desc">
              Enter your username and password below to view your study
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="login-card-content">
              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="patient-login-username"
                >
                  Username
                </label>
                <Input
                  id="patient-login-username"
                  label=""
                  type="text"
                  name="username"
                  placeholder="Enter Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={formErrors.username}
                  disabled={isLoading}
                  autoComplete="username"
                  required
                />
              </div>

              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="patient-login-password"
                >
                  Password
                </label>
                <Input
                  id="patient-login-password"
                  label=""
                  type="password"
                  name="password"
                  placeholder="Enter Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={formErrors.password}
                  showPasswordToggle
                  disabled={isLoading}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && !formErrors.username && !formErrors.password && (
                <div
                  className="error-message"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="login-btn-primary-light"
                isLoading={isLoading}
                disabled={isLoading}
              >
                Login
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PatientLogin;
